import { z } from 'zod';

/**
 * Workload & utilisation aggregation (M9 — PM-2056/PM-2057, DL-032).
 *
 * Pure: daily booking rows (already §1.4-gated by the repository) in, per-
 * resource per-period buckets out. Granularity is a PARAMETER, never a
 * variant (PM-2057). All date arithmetic is UTC epoch-day math on ISO strings
 * — never a local Date (DST-at-midnight zones break `+1 day`). All hour
 * arithmetic is integer hundredths (PM-2041 discipline); weighting multiplies
 * hundredths by an integer probability and divides by 100 only at the edge.
 */

export const UTILISATION_GRANULARITIES = ['day', 'week', 'month', 'quarter'] as const;
export type UtilisationGranularity = (typeof UTILISATION_GRANULARITIES)[number];
export const UtilisationGranularitySchema = z.enum(UTILISATION_GRANULARITIES);

export const UTILISATION_MODES = ['raw', 'weighted'] as const;
export type UtilisationMode = (typeof UTILISATION_MODES)[number];
export const UtilisationModeSchema = z.enum(UTILISATION_MODES);

export type WorkloadRow = {
  resourceId: string;
  date: string;
  hours: number;
  /** The owning project work item's probability; 100 when unset (DL-032). */
  probability: number;
  workItemId: string;
  allocationId: string;
};

export type CapacityDayRow = { resourceId: string; date: string; capacityHours: number };

const DAY_MS = 86_400_000;
const toUtc = (date: string): number => Date.parse(`${date}T00:00:00Z`);
const toIso = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/**
 * The bucket key a date falls in: day → the date itself; week → the ISO week's
 * Monday (weeks are Monday-start); month → `YYYY-MM`; quarter → `YYYY-Qn`
 * (calendar quarters).
 */
export const periodBucket = (date: string, granularity: UtilisationGranularity): string => {
  if (granularity === 'day') return date;
  if (granularity === 'week') {
    const ms = toUtc(date);
    const dayOfWeek = new Date(ms).getUTCDay(); // Sun 0 … Sat 6
    const sinceMonday = (dayOfWeek + 6) % 7;
    return toIso(ms - sinceMonday * DAY_MS);
  }
  if (granularity === 'month') return date.slice(0, 7);
  const month = Number(date.slice(5, 7));
  return `${date.slice(0, 4)}-Q${String(Math.floor((month - 1) / 3) + 1)}`;
};

/** Every bucket key intersecting [from, to], in order. */
export const enumerateBuckets = (
  from: string,
  to: string,
  granularity: UtilisationGranularity,
): string[] => {
  const buckets: string[] = [];
  const end = toUtc(to);
  let previous = '';
  for (let ms = toUtc(from); ms <= end; ms += DAY_MS) {
    const bucket = periodBucket(toIso(ms), granularity);
    if (bucket !== previous) {
      buckets.push(bucket);
      previous = bucket;
    }
  }
  return buckets;
};

export type UtilisationBucket = {
  period: string;
  workloadHours: number;
  capacityHours: number;
  /** workload ÷ capacity × 100, 0.01 precision; null when capacity is 0. */
  utilisationPct: number | null;
};

export type ResourceUtilisation = {
  resourceId: string;
  periods: UtilisationBucket[];
};

export type AggregateUtilisationOptions = {
  mode: UtilisationMode;
  granularity: UtilisationGranularity;
  from: string;
  to: string;
  /** Buckets are zero-filled for every id here even without any rows. */
  resourceIds: string[];
};

const h = (hours: number): number => Math.round(hours * 100);

/** Weighted contribution in hour-hundredths×100 (i.e. ten-thousandths of an hour). */
const contribution = (row: { hours: number; probability: number }, mode: UtilisationMode): number =>
  h(row.hours) * (mode === 'weighted' ? row.probability : 100);

/**
 * Per resource per period: workload (raw, or DL-032 probability-weighted),
 * capacity and utilisation. Iterative single pass over the rows; buckets are
 * enumerated from the range so quiet periods appear as zeros.
 */
export const aggregateUtilisation = (
  rows: WorkloadRow[],
  capacity: CapacityDayRow[],
  options: AggregateUtilisationOptions,
): ResourceUtilisation[] => {
  const buckets = enumerateBuckets(options.from, options.to, options.granularity);
  // resourceId → period → sums (workload in hour-ten-thousandths, capacity in hundredths).
  const workload = new Map<string, Map<string, number>>();
  const capacitySums = new Map<string, Map<string, number>>();
  for (const row of rows) {
    const period = periodBucket(row.date, options.granularity);
    const byPeriod = workload.get(row.resourceId) ?? new Map<string, number>();
    byPeriod.set(period, (byPeriod.get(period) ?? 0) + contribution(row, options.mode));
    workload.set(row.resourceId, byPeriod);
  }
  for (const row of capacity) {
    const period = periodBucket(row.date, options.granularity);
    const byPeriod = capacitySums.get(row.resourceId) ?? new Map<string, number>();
    byPeriod.set(period, (byPeriod.get(period) ?? 0) + h(row.capacityHours));
    capacitySums.set(row.resourceId, byPeriod);
  }

  return options.resourceIds.map((resourceId) => ({
    resourceId,
    periods: buckets.map((period) => {
      const workloadTenThousandths = workload.get(resourceId)?.get(period) ?? 0;
      const capacityHundredths = capacitySums.get(resourceId)?.get(period) ?? 0;
      const workloadHours = Math.round(workloadTenThousandths / 100) / 100;
      return {
        period,
        workloadHours,
        capacityHours: capacityHundredths / 100,
        utilisationPct:
          capacityHundredths === 0
            ? null
            : Math.round((workloadTenThousandths / capacityHundredths) * 100) / 100,
      };
    }),
  }));
};

// ---------------------------------------------------------------------------
// Competency (role) demand — PM-2056
// ---------------------------------------------------------------------------

export type GenericDemandRow = {
  roleId: string;
  date: string;
  hours: number;
  probability: number;
  workItemId: string;
  allocationId: string;
};

export type CompetencyDemandBucket = {
  period: string;
  /** Bookings of named resources holding the role (ALL role associations —
   * primary and secondary; whether a primary-role stamp should narrow this is
   * the parked M11 question). */
  namedHours: number;
  /** GENERIC allocations referencing the role — unresolved demand. */
  genericHours: number;
  totalHours: number;
};

export type RoleDemand = { roleId: string; periods: CompetencyDemandBucket[] };

export type AggregateCompetencyDemandOptions = {
  mode: UtilisationMode;
  granularity: UtilisationGranularity;
  from: string;
  to: string;
  roleIds: string[];
};

/**
 * Per role per period: named demand (workload rows of resources holding the
 * role, tagged with the roleId by the caller) + generic demand (GENERIC
 * allocations on the role), both DL-032-weighted in `weighted` mode.
 */
export const aggregateCompetencyDemand = (
  namedRows: (WorkloadRow & { roleId: string })[],
  genericRows: GenericDemandRow[],
  options: AggregateCompetencyDemandOptions,
): RoleDemand[] => {
  const buckets = enumerateBuckets(options.from, options.to, options.granularity);
  const named = new Map<string, Map<string, number>>();
  const generic = new Map<string, Map<string, number>>();
  const add = (
    target: Map<string, Map<string, number>>,
    roleId: string,
    date: string,
    value: number,
  ) => {
    const period = periodBucket(date, options.granularity);
    const byPeriod = target.get(roleId) ?? new Map<string, number>();
    byPeriod.set(period, (byPeriod.get(period) ?? 0) + value);
    target.set(roleId, byPeriod);
  };
  for (const row of namedRows) add(named, row.roleId, row.date, contribution(row, options.mode));
  for (const row of genericRows) {
    add(generic, row.roleId, row.date, contribution(row, options.mode));
  }

  const toHours = (tenThousandths: number): number => Math.round(tenThousandths / 100) / 100;
  return options.roleIds.map((roleId) => ({
    roleId,
    periods: buckets.map((period) => {
      const namedHours = toHours(named.get(roleId)?.get(period) ?? 0);
      const genericHours = toHours(generic.get(roleId)?.get(period) ?? 0);
      return {
        period,
        namedHours,
        genericHours,
        totalHours: Math.round((namedHours + genericHours) * 100) / 100,
      };
    }),
  }));
};
